import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CollectionResponse,
  EmptyResponse,
  IntakeIdentifier,
  Response,
  ZorgtopicsCarePlan,
  ZorgtopicsCarePlanPayload,
  ZorgtopicsChecklist,
  ZorgtopicsChecklistItemLink,
  ZorgtopicsChecklistPhaseMeetmoment,
  ZorgtopicsChecklistPayload,
  ZorgtopicsTrajectChecklist,
  ZorgtopicsCopyCoveragePayload,
  ZorgtopicsCoverage,
  ZorgtopicsCoveragePayload,
  ZorgtopicsCoverageProductPayload,
  ZorgtopicsAddendum,
  ZorgtopicsAddendumPayload,
  ZorgtopicsConfiguredConcern,
  ZorgtopicsDeliveryScope,
  ZorgtopicsCopyTarget,
  ZorgtopicsPracticeCandidate,
  ZorgtopicsOrganizationPractice,
  ZorgtopicsPracticeSummary,
  ZorgtopicsTrajectEligibility,
  ZorgtopicsGrantAddendaPayload,
  ZorgtopicsMembership,
  ZorgtopicsPractice,
} from "~/types/api";

export function createZorgtopicsApi({ getRequest, postRequest, putRequest, deleteRequest }: ApiRequestHelpers) {
  /** Fetches the national Zorgtopics care plan catalogue. */
  const zorgtopicsCarePlans = () => getRequest<CollectionResponse<ZorgtopicsCarePlan>>(`/zorgtopics/care-plans`);

  const createZorgtopicsCarePlan = (payload: ZorgtopicsCarePlanPayload) =>
    postRequest<Response<ZorgtopicsCarePlan>>(`/zorgtopics/care-plans`, payload);

  const updateZorgtopicsCarePlan = (carePlanId: string, payload: ZorgtopicsCarePlanPayload) =>
    putRequest<Response<ZorgtopicsCarePlan>>(`/zorgtopics/care-plans/${carePlanId}`, payload);

  const deleteZorgtopicsCarePlan = (carePlanId: string) =>
    deleteRequest<EmptyResponse>(`/zorgtopics/care-plans/${carePlanId}`);

  /** Fetches every checklist version, without phases. */
  const zorgtopicsChecklists = () => getRequest<CollectionResponse<ZorgtopicsChecklist>>(`/zorgtopics/checklists`);

  const zorgtopicsChecklist = (checklistId: string) =>
    getRequest<Response<ZorgtopicsChecklist>>(`/zorgtopics/checklists/${checklistId}`);

  const createZorgtopicsChecklist = (payload: ZorgtopicsChecklistPayload) =>
    postRequest<Response<ZorgtopicsChecklist>>(`/zorgtopics/checklists`, payload);

  /** Only a draft can be updated; the whole phase list is replaced. */
  const updateZorgtopicsChecklist = (checklistId: string, payload: ZorgtopicsChecklistPayload) =>
    putRequest<Response<ZorgtopicsChecklist>>(`/zorgtopics/checklists/${checklistId}`, payload);

  /** Sets the questionnaire of the steps and the meetmoment of the phases; also allowed on a published version. */
  const updateZorgtopicsChecklistItemLinks = (
    checklistId: string,
    items: ZorgtopicsChecklistItemLink[],
    phases: ZorgtopicsChecklistPhaseMeetmoment[],
  ) => putRequest<Response<ZorgtopicsChecklist>>(`/zorgtopics/checklists/${checklistId}/item-links`, { items, phases });

  const deleteZorgtopicsChecklist = (checklistId: string) =>
    deleteRequest<EmptyResponse>(`/zorgtopics/checklists/${checklistId}`);

  /** Copies a version into a new draft with the given version label. */
  const duplicateZorgtopicsChecklist = (checklistId: string, version: string) =>
    postRequest<Response<ZorgtopicsChecklist>>(`/zorgtopics/checklists/${checklistId}/duplicate`, { version });

  /** Makes this the active version of its slot and retires the previous one. */
  const activateZorgtopicsChecklist = (checklistId: string) =>
    postRequest<Response<ZorgtopicsChecklist>>(`/zorgtopics/checklists/${checklistId}/activate`);

  /** The checklist of the traject a treatment runs as, with what was ticked off. */
  const zorgtopicsTrajectChecklist = (treatmentId: number | string) =>
    getRequest<Response<ZorgtopicsTrajectChecklist>>(`/zorgtopics/treatments/${treatmentId}/checklist`);

  const checkZorgtopicsChecklistItem = (treatmentId: number | string, itemId: string) =>
    putRequest<Response<ZorgtopicsTrajectChecklist>>(`/zorgtopics/treatments/${treatmentId}/checklist/items/${itemId}`);

  const uncheckZorgtopicsChecklistItem = (treatmentId: number | string, itemId: string) =>
    deleteRequest<Response<ZorgtopicsTrajectChecklist>>(
      `/zorgtopics/treatments/${treatmentId}/checklist/items/${itemId}`,
    );

  /** Fetches the coverage matrix for a contract year, selected by period overlap. */
  const zorgtopicsCoverages = (contractYear: number) =>
    getRequest<CollectionResponse<ZorgtopicsCoverage>>(`/zorgtopics/coverages`, {
      params: { contract_year: contractYear },
    });

  /** Fetches the contract years the matrix can be shown for. */
  const zorgtopicsContractYears = () => getRequest<Response<number[]>>(`/zorgtopics/coverages/contract-years`);

  const createZorgtopicsCoverage = (payload: ZorgtopicsCoveragePayload) =>
    postRequest<Response<ZorgtopicsCoverage>>(`/zorgtopics/coverages`, payload);

  const updateZorgtopicsCoverage = (coverageId: string, payload: ZorgtopicsCoveragePayload) =>
    putRequest<Response<ZorgtopicsCoverage>>(`/zorgtopics/coverages/${coverageId}`, payload);

  const deleteZorgtopicsCoverage = (coverageId: string) =>
    deleteRequest<EmptyResponse>(`/zorgtopics/coverages/${coverageId}`);

  /** Replaces the whole prestatiecode mapping for a coverage. */
  const saveZorgtopicsCoverageProducts = (coverageId: string, products: ZorgtopicsCoverageProductPayload[]) =>
    putRequest<Response<ZorgtopicsCoverage>>(`/zorgtopics/coverages/${coverageId}/products`, { products });

  const activateZorgtopicsCoverage = (coverageId: string) =>
    postRequest<Response<ZorgtopicsCoverage>>(`/zorgtopics/coverages/${coverageId}/activate`);

  const deactivateZorgtopicsCoverage = (coverageId: string) =>
    postRequest<Response<ZorgtopicsCoverage>>(`/zorgtopics/coverages/${coverageId}/deactivate`);

  /** Copies a coverage and its mapping into a new period; the copy always lands as a draft. */
  const copyZorgtopicsCoverage = (coverageId: string, payload: ZorgtopicsCopyCoveragePayload) =>
    postRequest<Response<ZorgtopicsCoverage>>(`/zorgtopics/coverages/${coverageId}/copy`, payload);

  /** Read-only Zorgtopics setup of the caller's own practice; null when not affiliated. */
  const myZorgtopicsPractice = () => getRequest<Response<ZorgtopicsPracticeSummary | null>>(`/zorgtopics/my-practice`);

  /** Advisory pre-check for the start-treatment form; the start action re-checks server-side. */
  const zorgtopicsTrajectEligibility = (
    intakeId: IntakeIdentifier,
    params: { dcsph_code: string; csi_code: string; started_at: string },
  ) =>
    getRequest<Response<ZorgtopicsTrajectEligibility>>(`/zorgtopics/intakes/${intakeId}/traject-eligibility`, {
      params,
    });

  /** Whether the episode is reported in the monthly Zorgtopics data delivery. */
  const zorgtopicsDeliveryScope = (intakeId: IntakeIdentifier, params: { dcsph_code: string }) =>
    getRequest<Response<ZorgtopicsDeliveryScope>>(`/zorgtopics/intakes/${intakeId}/delivery-scope`, { params });

  /** Read-only roll-up of the caller's own organisation. */
  const myZorgtopicsOrganization = () =>
    getRequest<Response<ZorgtopicsOrganizationPractice[]>>(`/zorgtopics/my-organization`);

  /** Fetches the practices that participate in Zorgtopics, with membership and addenda. */
  const zorgtopicsPractices = () => getRequest<CollectionResponse<ZorgtopicsPractice>>(`/zorgtopics/practices`);

  /** The concerns and care plans an addendum can be granted for, from the coverage matrix. */
  const zorgtopicsConfiguredConcerns = () =>
    getRequest<Response<ZorgtopicsConfiguredConcern[]>>(`/zorgtopics/practices/configured-concerns`);

  /** Practices that can be affiliated, matched on name, AGB code or organisation. */
  const zorgtopicsPracticeCandidates = (search: string) =>
    getRequest<Response<ZorgtopicsPracticeCandidate[]>>(`/zorgtopics/practices/candidates`, { params: { search } });

  /** Practices this setup may be copied to: same organisation, not already affiliated. */
  const zorgtopicsCopyTargets = (practiceId: string) =>
    getRequest<Response<ZorgtopicsCopyTarget[]>>(`/zorgtopics/practices/${practiceId}/copy-targets`);

  /** Copies a practice's membership and addenda onto another practice. */
  const copyZorgtopicsPractice = (practiceId: string, targetPracticeId: string) =>
    postRequest<EmptyResponse>(`/zorgtopics/practices/${practiceId}/copy`, {
      target_practice_id: targetPracticeId,
    });

  /** Sets a practice's membership; null takes it out of Zorgtopics. */
  const setZorgtopicsMembership = (practiceId: string, membership: ZorgtopicsMembership | null) =>
    putRequest<EmptyResponse>(`/zorgtopics/practices/${practiceId}/membership`, { membership });

  /** Grants one addendum per selected care plan over the same period, atomically. */
  const grantZorgtopicsAddenda = (practiceId: string, payload: ZorgtopicsGrantAddendaPayload) =>
    postRequest<CollectionResponse<ZorgtopicsAddendum>>(`/zorgtopics/practices/${practiceId}/addenda`, payload);

  const updateZorgtopicsAddendum = (addendumId: string, payload: ZorgtopicsAddendumPayload) =>
    putRequest<Response<ZorgtopicsAddendum>>(`/zorgtopics/addenda/${addendumId}`, payload);

  const revokeZorgtopicsAddendum = (addendumId: string) =>
    deleteRequest<EmptyResponse>(`/zorgtopics/addenda/${addendumId}`);

  return {
    zorgtopicsChecklists,
    zorgtopicsChecklist,
    createZorgtopicsChecklist,
    updateZorgtopicsChecklist,
    deleteZorgtopicsChecklist,
    updateZorgtopicsChecklistItemLinks,
    duplicateZorgtopicsChecklist,
    activateZorgtopicsChecklist,
    zorgtopicsTrajectChecklist,
    checkZorgtopicsChecklistItem,
    uncheckZorgtopicsChecklistItem,
    myZorgtopicsPractice,
    zorgtopicsTrajectEligibility,
    zorgtopicsDeliveryScope,
    myZorgtopicsOrganization,
    zorgtopicsPractices,
    zorgtopicsConfiguredConcerns,
    zorgtopicsPracticeCandidates,
    zorgtopicsCopyTargets,
    copyZorgtopicsPractice,
    setZorgtopicsMembership,
    grantZorgtopicsAddenda,
    updateZorgtopicsAddendum,
    revokeZorgtopicsAddendum,
    zorgtopicsCarePlans,
    createZorgtopicsCarePlan,
    updateZorgtopicsCarePlan,
    deleteZorgtopicsCarePlan,
    zorgtopicsCoverages,
    zorgtopicsContractYears,
    createZorgtopicsCoverage,
    updateZorgtopicsCoverage,
    deleteZorgtopicsCoverage,
    saveZorgtopicsCoverageProducts,
    activateZorgtopicsCoverage,
    deactivateZorgtopicsCoverage,
    copyZorgtopicsCoverage,
  };
}

export type ZorgtopicsApi = ReturnType<typeof createZorgtopicsApi>;
